import { Anchor, Button, Flex, Notification, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'i18n';

import { breakpoints } from 'styles/theme.variables';

const NS = 'dashBanner';

interface DashBannerProps {
  button?: {
    label: string;
    leftSection?: React.ReactNode;
    onClick: () => void;
    rightSection?: React.ReactNode;
    variant?: 'filled' | 'outline-base';
  };
  icon?: React.ReactNode;
  isIconStackedOnMobile?: boolean;
  isInlineOnMobile?: boolean;
  link?: {
    label: string;
    href?: string;
    target?: string;
    onClick?: () => void;
  };
  message: string | React.ReactNode;
  onClose?: () => void;
  title?: string;
  variant?: 'yellow' | 'green' | 'blue' | 'pink' | 'gray';
  withCloseButton?: boolean;
}

function LinkComponent({ link }: { link: DashBannerProps['link'] }) {
  if (link?.onClick) {
    return (
      <Button variant="link" onClick={link?.onClick} c="var(--content-accent-primary)">
        {link.label}
      </Button>
    );
  }

  if (link?.href) {
    return (
      <Anchor href={link.href} target={link.target}>
        {link.label}
      </Anchor>
    );
  }
}

export default function DashBanner({
  button,
  icon,
  isIconStackedOnMobile,
  isInlineOnMobile,
  link,
  message,
  onClose = () => {},
  title,
  withCloseButton,
  variant = 'yellow',
}: DashBannerProps) {
  const { t } = useTranslation(NS);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const isIconStacked = isMobile && isIconStackedOnMobile;

  return (
    <Notification
      icon={icon}
      variant={variant}
      withCloseButton={withCloseButton}
      closeButtonProps={{ 'aria-label': t('DashBanner.closeButton') }}
      onClose={onClose}
      styles={{
        title: { marginBottom: 0, lineHeight: 0 },
        root: isIconStacked ? { alignItems: 'flex-start', flexDirection: 'column' } : undefined,
        icon: isIconStacked
          ? { marginBottom: 'var(--mantine-spacing-xs)', marginInlineEnd: 0 }
          : { marginInlineEnd: 'var(--mantine-spacing-xs)' },
        closeButton: isMobile
          ? {
              position: 'absolute',
              right: 'var(--mantine-spacing-sm)',
              top: 'var(--mantine-spacing-sm)',
            }
          : { marginLeft: 'var(--mantine-spacing-xs)' },
      }}
    >
      <Flex
        direction={isMobile && !isInlineOnMobile ? 'column' : 'row'}
        align={isMobile && !isInlineOnMobile ? 'flex-start' : 'center'}
        gap={isMobile && !isInlineOnMobile ? 'xs' : ''}
      >
        <Flex direction="column" gap={isMobile ? 'xs' : 'xs3'} justify="flex-start">
          {title && <Text variant="title-sm">{title}</Text>}
          <Text variant="body-sm">{message}</Text>
          {!isMobile && link && <LinkComponent link={link} />}
        </Flex>
        <Flex flex={1} justify={isMobile ? 'flex-start' : 'flex-end'}>
          {button && (
            <Button
              variant={button.variant ?? 'outline-base'}
              onClick={button.onClick}
              size="sm"
              ml={isMobile ? '0' : 'xs'}
              leftSection={button?.leftSection}
              rightSection={button?.rightSection}
            >
              {button.label}
            </Button>
          )}
        </Flex>
        {isMobile && link && <LinkComponent link={link} />}
      </Flex>
    </Notification>
  );
}
